import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import PieChartRoundedIcon from "@mui/icons-material/PieChartRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StackedLineChartRoundedIcon from "@mui/icons-material/StackedLineChartRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import dayjs from "dayjs";

const TOKENS = {
  colors: {
    palette: ["#2563EB", "#7C3AED", "#DB2777", "#0891B2", "#D97706", "#16A34A", "#DC2626", "#4F46E5"],
    surface: "#FFFFFF",
    surfaceMuted: "#F8FAFC",
    border: "rgba(15,23,42,0.07)",
    textPrimary: "#0F172A",
    textSecondary: "rgba(15,23,42,0.55)",
    textTertiary: "rgba(15,23,42,0.4)",
  },
  radius: { sm: "8px", md: "10px", lg: "16px" },
  shadow: {
    card: "0 6px 18px rgba(15,23,42,0.04)",
    tooltip: "0 8px 24px rgba(15,23,42,0.10)",
  },
};

const POINT_WIDTH = 42;
const MIN_CHART_WIDTH = 320;
const MIN_ZOOM_POINTS = 2;
const BAR_GROUP_MIN_WIDTH = 56;
const BAR_WIDTH_PER_SERIES = 20;

const SCROLL_X_SX = {
  height: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": { height: 6 },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(15,23,42,0.15)",
    borderRadius: 3,
  },
};

const formatNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : "0";
};

const formatCurrency = (value) => `₹${formatNumber(value)}`;

const formatDate = (value) => {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : "—";
};

const formatChartDate = (value) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM") : String(value);
};

const formatAxisNumber = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n) >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
  if (Math.abs(n) >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const sameArray = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

const buildStepAxis = (data = [], step = 50, min = 0) => {
  let max = 0;

  data.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (key === "date") return;
      const value = row[key];
      if (typeof value === "number" && Number.isFinite(value) && value > max) {
        max = value;
      }
    });
  });

  const floor = Math.floor(min / step) * step;
  const top = Math.max(floor + step, Math.ceil((max || floor) / step) * step);
  const ticks = [];
  for (let t = floor; t <= top; t += step) {
    ticks.push(t);
  }

  return { domain: [floor, top], ticks };
};

const createFallbackSeries = (rows = []) => {
  const map = {};

  rows.forEach((item) => {
    const variety = String(item?.variety || "Unknown").trim();

    if (!map[variety]) {
      map[variety] = { variety, totalArrival: 0, data: [] };
    }

    map[variety].data.push({
      date: item.date,
      variety,
      arrival: Number(item.arrival || 0),
      handArrival: Number(item.handArrival || 0),
      combineArrival: Number(item.combineArrival || 0),
      priceMin: Number(item.priceMin || 0) > 0 ? Number(item.priceMin) : null,
      priceMax: Number(item.priceMax || 0) > 0 ? Number(item.priceMax) : null,
      modalPrice: Number(item.modalPrice || 0) > 0 ? Number(item.modalPrice) : null,
    });

    map[variety].totalArrival += Number(item.arrival || 0);
  });

  return Object.values(map);
};


const StatBlock = ({ label, value, accent }) => (
  <Box
    sx={{
      px: 1.5,
      py: 0.75,
      borderRadius: TOKENS.radius.sm,
      background: TOKENS.colors.surface,
      border: `1px solid ${TOKENS.colors.border}`,
      minWidth: { xs: 92, sm: 108 },
      flex: { xs: "1 1 30%", sm: "0 0 auto" },
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "0.58rem", sm: "0.66rem" },
        fontWeight: 700,
        color: TOKENS.colors.textTertiary,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </Typography>
    <Typography
      noWrap
      sx={{
        fontSize: { xs: "0.8rem", sm: "1rem" },
        fontWeight: 800,
        color: accent || TOKENS.colors.textPrimary,
        lineHeight: 1.3,
      }}
    >
      {value}
    </Typography>
  </Box>
);


const ChartLegend = ({ items, colorMap, hidden, onToggle }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
    {items.map((variety) => {
      const isHidden = hidden.has(variety);
      return (
        <Box
          key={variety}
          onClick={() => onToggle(variety)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            cursor: "pointer",
            px: 1,
            py: 0.35,
            borderRadius: "6px",
            opacity: isHidden ? 0.4 : 1,
            "&:hover": { background: "rgba(15,23,42,0.04)" },
          }}
        >
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: "3px",
              background: colorMap[variety],
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: "0.74rem",
              fontWeight: 600,
              color: TOKENS.colors.textPrimary,
              textDecoration: isHidden ? "line-through" : "none",
            }}
          >
            {variety}
          </Typography>
        </Box>
      );
    })}
  </Stack>
);

const ChartPanel = ({
  icon: Icon,
  iconColor,
  title,
  subtitle,
  emptyLabel,
  chartType,
  onChartTypeChange,
  lineData,
  pieData,
  selectedSeries,
  colorMap,
  valueFormatter,
  axisFormatter,
  yAxisStep,
  yAxisMin = 0,
  altChartType = "pie", 
  tooltipNameSuffix = "",
  loading,
  height = 320,
}) => {
  const [hidden, setHidden] = useState(new Set());


  const [zoomRange, setZoomRange] = useState(null);
  const canDragZoom = useMediaQuery("(pointer: fine)");

  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const dragRef = useRef({ dragging: false, startX: 0, currentX: 0, rectLeft: 0, rectWidth: 0 });
  const rafRef = useRef(null);
  const activeListenersRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    setHidden(new Set());
  }, [selectedSeries.length]);

  useEffect(() => {
    setZoomRange(null);
  }, [lineData]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (activeListenersRef.current) {
        window.removeEventListener("mousemove", activeListenersRef.current.move);
        window.removeEventListener("mouseup", activeListenersRef.current.up);
      }
      document.body.style.userSelect = "";
    };
  }, []);

  const toggleHidden = (variety) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(variety)) next.delete(variety);
      else next.add(variety);
      return next;
    });
  };

  const visibleSeries = selectedSeries.filter((item) => !hidden.has(item.variety));
  const visiblePieData = pieData.filter((item) => !hidden.has(item.variety));

  const displayData = useMemo(() => {
    if (!zoomRange) return lineData;
    return lineData.slice(zoomRange.startIndex, zoomRange.endIndex + 1);
  }, [lineData, zoomRange]);

  const isZoomed = Boolean(zoomRange);
  const hasData =
    chartType === "pie"
      ? visiblePieData.length
      : (chartType === "bar" ? lineData : displayData).length && visibleSeries.length;

  const axisSource = chartType === "bar" ? lineData : displayData;

  const yAxisConfig = useMemo(() => {
    if (!yAxisStep) return null;
    return buildStepAxis(axisSource, yAxisStep, yAxisMin);
  }, [axisSource, yAxisStep, yAxisMin]);

  const barData = useMemo(() => {
    if (!yAxisMin) return lineData;
    return lineData.map((row) => {
      const next = { ...row };
      Object.keys(next).forEach((key) => {
        if (key !== "date" && typeof next[key] === "number" && next[key] < yAxisMin) {
          next[key] = null;
        }
      });
      return next;
    });
  }, [lineData, yAxisMin]);

  const resetZoom = useCallback(() => {
    setZoomRange(null);
  }, []);

  const pixelToIndex = useCallback(
    (x, rectWidth) => {
      if (!displayData.length) return 0;
      const leftOffset = 48;
      const rightOffset = 16;
      const plotWidth = Math.max(rectWidth - leftOffset - rightOffset, 1);
      const ratio = (x - leftOffset) / plotWidth;
      const index = Math.round(ratio * (displayData.length - 1));
      return Math.min(Math.max(index, 0), displayData.length - 1);
    },
    [displayData.length]
  );

  const updateOverlay = useCallback((startX, currentX) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const left = Math.min(startX, currentX);
    const width = Math.abs(currentX - startX);
    overlay.style.left = `${left}px`;
    overlay.style.width = `${width}px`;
    overlay.style.display = width > 3 ? "block" : "none";
  }, []);

  const handlePointerDown = useCallback(
    (event) => {
      if (!canDragZoom || chartType !== "line") return;
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const startX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);

      dragRef.current = {
        dragging: true,
        startX,
        currentX: startX,
        rectLeft: rect.left,
        rectWidth: rect.width,
      };
      updateOverlay(startX, startX);
      document.body.style.userSelect = "none";

      const handleMove = (moveEvent) => {
        if (!dragRef.current.dragging) return;
        if (rafRef.current) return; 
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          const { rectLeft, rectWidth } = dragRef.current;
          const currentX = Math.min(Math.max(moveEvent.clientX - rectLeft, 0), rectWidth);
          dragRef.current.currentX = currentX;
          updateOverlay(dragRef.current.startX, currentX);
        });
      };

      const handleUp = () => {
        window.removeEventListener("mousemove", handleMove);
        window.removeEventListener("mouseup", handleUp);
        activeListenersRef.current = null;
        document.body.style.userSelect = "";
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        if (overlayRef.current) overlayRef.current.style.display = "none";

        const { startX: sX, currentX: cX, rectWidth } = dragRef.current;
        dragRef.current.dragging = false;

        if (cX === undefined || Math.abs(cX - sX) < 4) return; // treat as a click, not a drag

        const leftIndex = pixelToIndex(Math.min(sX, cX), rectWidth);
        const rightIndex = pixelToIndex(Math.max(sX, cX), rectWidth);

        if (rightIndex - leftIndex + 1 >= MIN_ZOOM_POINTS && mountedRef.current) {
          setZoomRange((prevRange) => {
            const base = prevRange ? prevRange.startIndex : 0;
            return { startIndex: base + leftIndex, endIndex: base + rightIndex };
          });
        }
      };

      activeListenersRef.current = { move: handleMove, up: handleUp };
      window.addEventListener("mousemove", handleMove, { passive: true });
      window.addEventListener("mouseup", handleUp, { passive: true });
    },
    [canDragZoom, chartType, pixelToIndex, updateOverlay]
  );

  const chartWidthPx = Math.max(displayData.length * POINT_WIDTH, MIN_CHART_WIDTH);

  const barGroupWidth = Math.max(visibleSeries.length * BAR_WIDTH_PER_SERIES + 24, BAR_GROUP_MIN_WIDTH);
  const barChartWidthPx = Math.max(lineData.length * barGroupWidth, MIN_CHART_WIDTH);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, height: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.25}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "9px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `${iconColor}18`,
              color: iconColor,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 17 }} />
          </Box>
          <Box>
            <Typography fontWeight={800} sx={{ fontSize: "0.88rem" }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: "0.71rem", color: TOKENS.colors.textSecondary }}>{subtitle}</Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {chartType === "line" && isZoomed && (
            <MuiTooltip title="Reset zoom">
              <Chip
                size="small"
                onClick={resetZoom}
                icon={<RestartAltRoundedIcon sx={{ fontSize: 14 }} />}
                label="Reset zoom"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  cursor: "pointer",
                  background: "rgba(37,99,235,0.10)",
                  color: "#2563EB",
                }}
              />
            </MuiTooltip>
          )}

          <ToggleButtonGroup
            value={chartType}
            exclusive
            size="small"
            onChange={(_e, value) => value && onChartTypeChange(value)}
            sx={{
              background: TOKENS.colors.surfaceMuted,
              borderRadius: TOKENS.radius.md,
              p: 0.35,
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "8px !important",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.72rem",
                color: TOKENS.colors.textSecondary,
                px: 1.2,
                py: 0.4,
                gap: 0.5,
              },
              "& .Mui-selected": {
                background: "#fff !important",
                color: `${TOKENS.colors.textPrimary} !important`,
                boxShadow: "0 2px 6px rgba(15,23,42,0.12)",
              },
            }}
          >
            <ToggleButton value="line">
              <ShowChartRoundedIcon sx={{ fontSize: 15 }} />
              Line
            </ToggleButton>
            <ToggleButton value={altChartType}>
              {altChartType === "bar" ? (
                <BarChartRoundedIcon sx={{ fontSize: 15 }} />
              ) : (
                <PieChartRoundedIcon sx={{ fontSize: 15 }} />
              )}
              {altChartType === "bar" ? "Bar" : "Pie"}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {loading ? (
        <Skeleton variant="rounded" height={height} sx={{ mt: 2, borderRadius: TOKENS.radius.md }} />
      ) : (
        <>
          <Box sx={{ height, mt: 1 }}>
            {!hasData ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 0.5,
                  color: "text.secondary",
                }}
              >
                <Icon sx={{ fontSize: 34, opacity: 0.2 }} />
                <Typography fontWeight={700} sx={{ fontSize: "0.82rem" }}>
                  {emptyLabel}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: TOKENS.colors.textTertiary }}>
                  Select a variety above to plot it here
                </Typography>
              </Box>
            ) : chartType === "line" ? (
              <Box
                sx={{
                  height: "100%",
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": { height: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(15,23,42,0.15)",
                    borderRadius: 3,
                  },
                }}
              >
                <Box
                  ref={containerRef}
                  onMouseDown={handlePointerDown}
                  sx={{
                    position: "relative",
                    minWidth: `${chartWidthPx}px`,
                    height: "100%",
                    cursor: canDragZoom ? "crosshair" : "default",
                  }}
                >
                  <Box
                    ref={overlayRef}
                    sx={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      display: "none",
                      background: "rgba(37,99,235,0.12)",
                      borderLeft: "1px solid rgba(37,99,235,0.45)",
                      borderRight: "1px solid rgba(37,99,235,0.45)",
                      pointerEvents: "none",
                      zIndex: 2,
                    }}
                  />
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={displayData} margin={{ top: 6, right: 16, left: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.22} vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatChartDate}
                        tick={{ fontSize: 11 }}
                        minTickGap={20}
                        axisLine={{ stroke: TOKENS.colors.border }}
                        tickLine={false}
                        allowDataOverflow
                      />
                      <YAxis
                        tickFormatter={axisFormatter}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        width={44}
                        domain={yAxisConfig ? yAxisConfig.domain : undefined}
                        ticks={yAxisConfig ? yAxisConfig.ticks : undefined}
                        allowDataOverflow
                      />
                      <Tooltip
                        labelFormatter={(value) => formatDate(value)}
                        formatter={(value, name) =>
                          value === null || value === undefined
                            ? ["No data", `${name}${tooltipNameSuffix}`]
                            : [valueFormatter(value), `${name}${tooltipNameSuffix}`]
                        }
                        contentStyle={{
                          borderRadius: TOKENS.radius.md,
                          border: `1px solid ${TOKENS.colors.border}`,
                          boxShadow: TOKENS.shadow.tooltip,
                          fontSize: "0.78rem",
                        }}
                      />
                      {visibleSeries.map((item) => (
                        <Line
                          key={item.variety}
                          type="monotone"
                          dataKey={item.variety}
                          name={item.variety}
                          stroke={colorMap[item.variety]}
                          strokeWidth={2.5}
                          dot={false}
                          connectNulls={false}
                          activeDot={{ r: 4.5 }}
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            ) : chartType === "bar" ? (
              <Box sx={SCROLL_X_SX}>
                <Box sx={{ minWidth: `${barChartWidthPx}px`, height: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barData}
                      margin={{ top: 6, right: 16, left: 4, bottom: 0 }}
                      barCategoryGap="18%"
                      barGap={2}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.22} vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatChartDate}
                        tick={{ fontSize: 11 }}
                        interval={0}
                        axisLine={{ stroke: TOKENS.colors.border }}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={axisFormatter}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        width={44}
                        domain={yAxisConfig ? yAxisConfig.domain : undefined}
                        ticks={yAxisConfig ? yAxisConfig.ticks : undefined}
                        allowDataOverflow
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(15,23,42,0.04)" }}
                        labelFormatter={(value) => formatDate(value)}
                        formatter={(value, name) =>
                          value === null || value === undefined
                            ? ["No data", `${name}${tooltipNameSuffix}`]
                            : [valueFormatter(value), `${name}${tooltipNameSuffix}`]
                        }
                        contentStyle={{
                          borderRadius: TOKENS.radius.md,
                          border: `1px solid ${TOKENS.colors.border}`,
                          boxShadow: TOKENS.shadow.tooltip,
                          fontSize: "0.78rem",
                        }}
                      />
                      {visibleSeries.map((item) => (
                        <Bar
                          key={item.variety}
                          dataKey={item.variety}
                          name={item.variety}
                          fill={colorMap[item.variety]}
                          radius={[4, 4, 0, 0]}
                          maxBarSize={22}
                          isAnimationActive={false}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={visiblePieData}
                    dataKey="value"
                    nameKey="variety"
                    innerRadius="58%"
                    outerRadius="82%"
                    paddingAngle={2}
                    strokeWidth={1}
                  >
                    {visiblePieData.map((entry) => (
                      <Cell key={entry.variety} fill={colorMap[entry.variety]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [valueFormatter(value), name]}
                    contentStyle={{
                      borderRadius: TOKENS.radius.md,
                      border: `1px solid ${TOKENS.colors.border}`,
                      boxShadow: TOKENS.shadow.tooltip,
                      fontSize: "0.78rem",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>

          {chartType === "bar" && hasData && (
            <Typography sx={{ mt: 0.75, fontSize: "0.68rem", color: TOKENS.colors.textTertiary }}>
              Bars are grouped by date. Scroll sideways for more dates.
            </Typography>
          )}

          {chartType === "line" && hasData && (
            <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.75 }}>
              <ZoomInRoundedIcon sx={{ fontSize: 13, color: TOKENS.colors.textTertiary }} />
              <Typography sx={{ fontSize: "0.68rem", color: TOKENS.colors.textTertiary }}>
                {isZoomed
                  ? "Drag again to zoom further, or reset zoom above."
                  : "Drag across the chart to zoom in on overlapping lines. Scroll sideways for more dates."}
              </Typography>
            </Stack>
          )}

          {selectedSeries.length > 0 && (
            <ChartLegend
              items={selectedSeries.map((item) => item.variety)}
              colorMap={colorMap}
              hidden={hidden}
              onToggle={toggleHidden}
            />
          )}
        </>
      )}
    </Box>
  );
};

const IVRDashboardCharts = ({
  series = [],
  fallbackRows = [],
  selectedVarieties = [],
  onSelectionChange,
  dateFrom,
  dateTo,
  days = 15,
  maxSelections = 5,
  loading = false,
}) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("lg"));

  const [arrivalChartType, setArrivalChartType] = useState("line");
  const [priceChartType, setPriceChartType] = useState("line");
  const [varietySearch, setVarietySearch] = useState("");

  const chartSeries = useMemo(() => {
    if (Array.isArray(series) && series.length > 0) return series;
    return createFallbackSeries(fallbackRows);
  }, [series, fallbackRows]);

  const sortedSeries = useMemo(() => {
    return [...chartSeries].sort((a, b) => Number(b.totalArrival || 0) - Number(a.totalArrival || 0));
  }, [chartSeries]);

  useEffect(() => {
    if (!sortedSeries.length) {
      if (selectedVarieties.length && onSelectionChange) onSelectionChange([]);
      return;
    }

    const available = sortedSeries.map((item) => item.variety);
    const valid = selectedVarieties.filter((value) => available.includes(value));

    let next;
    if (selectedVarieties.length === 0 || valid.length === 0) {
      next = available.slice(0, maxSelections);
    } else {
      next = valid.slice(0, maxSelections);
    }

    if (!sameArray(selectedVarieties, next) && onSelectionChange) {
      onSelectionChange(next);
    }
  }, [sortedSeries, selectedVarieties, maxSelections, onSelectionChange]);

  const colorMap = useMemo(() => {
    const map = {};
    sortedSeries.forEach((item, index) => {
      map[item.variety] = TOKENS.colors.palette[index % TOKENS.colors.palette.length];
    });
    return map;
  }, [sortedSeries]);

  const selectedSeries = useMemo(() => {
    return sortedSeries.filter((item) => selectedVarieties.includes(item.variety));
  }, [sortedSeries, selectedVarieties]);

  const filteredChipList = useMemo(() => {
    const term = varietySearch.trim().toLowerCase();
    if (!term) return sortedSeries;
    return sortedSeries.filter((item) => item.variety.toLowerCase().includes(term));
  }, [sortedSeries, varietySearch]);

  const toggleVariety = (variety) => {
    const isSelected = selectedVarieties.includes(variety);

    if (isSelected) {
      onSelectionChange?.(selectedVarieties.filter((v) => v !== variety));
      return;
    }

    if (selectedVarieties.length >= maxSelections) return;
    onSelectionChange?.([...selectedVarieties, variety]);
  };

  const handleSelectAll = () => {
    onSelectionChange?.(sortedSeries.map((item) => item.variety).slice(0, maxSelections));
  };

  const handleClearAll = () => onSelectionChange?.([]);

  const handleDropdownChange = (event) => {
    const { value } = event.target;
    const arr = typeof value === "string" ? value.split(",") : value;
    onSelectionChange?.(arr.slice(0, maxSelections));
  };

  const arrivalChartData = useMemo(() => {
    const dateMap = {};
    selectedSeries.forEach((seriesItem) => {
      (seriesItem.data || []).forEach((item) => {
        if (!dateMap[item.date]) dateMap[item.date] = { date: item.date };
        dateMap[item.date][seriesItem.variety] = Number(item.arrival || 0);
      });
    });
    return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [selectedSeries]);

  const priceChartData = useMemo(() => {
    const dateMap = {};
    selectedSeries.forEach((seriesItem) => {
      (seriesItem.data || []).forEach((item) => {
        if (!dateMap[item.date]) dateMap[item.date] = { date: item.date };
        dateMap[item.date][seriesItem.variety] = Number(item.modalPrice) > 0 ? Number(item.modalPrice) : null;
      });
    });
    return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [selectedSeries]);

  const arrivalPieData = useMemo(() => {
    return selectedSeries
      .map((item) => ({
        variety: item.variety,
        value: (item.data || []).reduce((sum, row) => sum + Number(row.arrival || 0), 0),
      }))
      .filter((item) => item.value > 0);
  }, [selectedSeries]);

  const pricePieData = useMemo(() => {
    return selectedSeries
      .map((item) => {
        const withPrice = [...(item.data || [])]
          .filter((row) => row.modalPrice !== null && row.modalPrice !== undefined)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        return { variety: item.variety, value: withPrice.length ? Number(withPrice[0].modalPrice) : 0 };
      })
      .filter((item) => item.value > 0);
  }, [selectedSeries]);

  const totalArrivalSelected = useMemo(
    () => arrivalPieData.reduce((sum, item) => sum + item.value, 0),
    [arrivalPieData]
  );

  const avgModalPrice = useMemo(() => {
    if (!pricePieData.length) return null;
    return pricePieData.reduce((sum, item) => sum + item.value, 0) / pricePieData.length;
  }, [pricePieData]);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: TOKENS.radius.lg,
        border: `1px solid ${TOKENS.colors.border}`,
        boxShadow: TOKENS.shadow.card,
        overflow: "hidden",
        mb: 3,
      }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2.5 }, background: TOKENS.colors.surfaceMuted }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1.5}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: TOKENS.radius.md,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <StackedLineChartRoundedIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box>
              <Typography fontWeight={800} sx={{ fontSize: "0.95rem" }}>
                Variety Comparison
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: TOKENS.colors.textSecondary }}>
                {dateFrom && dateTo ? `${formatDate(dateFrom)} → ${formatDate(dateTo)}` : `Last ${days} days`}
                {" · one filter drives arrival and price below"}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ width: { xs: "100%", md: "auto" }, rowGap: 1 }}
          >
            <StatBlock label="Varieties" value={`${selectedVarieties.length}/${sortedSeries.length}`} />
            <StatBlock label="Total Arrival" value={formatNumber(totalArrivalSelected)} accent="#2563EB" />
            <StatBlock
              label="Avg Modal Price"
              value={avgModalPrice ? formatCurrency(Math.round(avgModalPrice)) : "—"}
              accent="#7C3AED"
            />
          </Stack>
        </Stack>

        {isCompact ? (
          <Stack sx={{ mt: 2 }} spacing={1}>
            <Select
              multiple
              size="small"
              displayEmpty
              value={selectedVarieties}
              onChange={handleDropdownChange}
              input={
                <OutlinedInput
                  sx={{
                    background: "#fff",
                    borderRadius: TOKENS.radius.sm,
                  }}
                />
              }
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <Typography sx={{ fontSize: "0.82rem", color: TOKENS.colors.textTertiary }}>
                    Select varieties…
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {selected.map((variety) => (
                      <Chip
                        key={variety}
                        label={variety}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          background: `${colorMap[variety]}18`,
                          color: TOKENS.colors.textPrimary,
                        }}
                      />
                    ))}
                  </Stack>
                )
              }
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 360 },
                },
              }}
              sx={{
                width: "100%",
                "& .MuiSelect-select": { py: 1 },
              }}
            >
              <Box sx={{ px: 1.5, py: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  placeholder="Search variety..."
                  value={varietySearch}
                  onChange={(e) => setVarietySearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ fontSize: 18, color: TOKENS.colors.textTertiary }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {filteredChipList.length === 0 && (
                <MenuItem disabled>
                  <Typography sx={{ fontSize: "0.78rem", color: TOKENS.colors.textTertiary }}>
                    No varieties match "{varietySearch}"
                  </Typography>
                </MenuItem>
              )}

              {filteredChipList.map((item) => {
                const isSelected = selectedVarieties.includes(item.variety);
                const disabled = !isSelected && selectedVarieties.length >= maxSelections;

                return (
                  <MenuItem key={item.variety} value={item.variety} disabled={disabled}>
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        color: colorMap[item.variety],
                        "&.Mui-checked": { color: colorMap[item.variety] },
                      }}
                    />
                    <ListItemText
                      primary={item.variety}
                      secondary={`${formatNumber(item.totalArrival)} total arrival`}
                      primaryTypographyProps={{ fontSize: "0.82rem", fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: "0.68rem" }}
                    />
                  </MenuItem>
                );
              })}
            </Select>

            <Stack direction="row" spacing={1}>
              <MuiTooltip title={`Select top ${maxSelections} by arrival`}>
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  startIcon={<DoneAllRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: TOKENS.radius.sm, fontSize: "0.76rem" }}
                >
                  Top {maxSelections}
                </Button>
              </MuiTooltip>

              <Button
                size="small"
                onClick={handleClearAll}
                startIcon={<ClearRoundedIcon sx={{ fontSize: 15 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: TOKENS.radius.sm,
                  fontSize: "0.76rem",
                  color: TOKENS.colors.textSecondary,
                }}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        ) : (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }} alignItems={{ xs: "stretch", sm: "center" }}>
              <TextField
                size="small"
                placeholder="Search variety..."
                value={varietySearch}
                onChange={(e) => setVarietySearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: 18, color: TOKENS.colors.textTertiary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 220 },
                  background: "#fff",
                  "& .MuiOutlinedInput-root": { borderRadius: TOKENS.radius.sm },
                }}
              />

              <MuiTooltip title={`Select top ${maxSelections} by arrival`}>
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  startIcon={<DoneAllRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: TOKENS.radius.sm, fontSize: "0.76rem" }}
                >
                  Top {maxSelections}
                </Button>
              </MuiTooltip>

              <Button
                size="small"
                onClick={handleClearAll}
                startIcon={<ClearRoundedIcon sx={{ fontSize: 15 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: TOKENS.radius.sm,
                  fontSize: "0.76rem",
                  color: TOKENS.colors.textSecondary,
                }}
              >
                Clear
              </Button>
            </Stack>

            <Box
              sx={{
                mt: 1.25,
                maxHeight: 92,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {filteredChipList.length === 0 && (
                  <Typography sx={{ fontSize: "0.78rem", color: TOKENS.colors.textTertiary, py: 1 }}>
                    No varieties match "{varietySearch}"
                  </Typography>
                )}

                {filteredChipList.map((item) => {
                  const isSelected = selectedVarieties.includes(item.variety);
                  const disabled = !isSelected && selectedVarieties.length >= maxSelections;
                  const color = colorMap[item.variety];

                  return (
                    <MuiTooltip
                      key={item.variety}
                      title={
                        disabled
                          ? `Max ${maxSelections} varieties — remove one to add this`
                          : `${formatNumber(item.totalArrival)} total arrival`
                      }
                    >
                      <span>
                        <Chip
                          clickable
                          disabled={disabled}
                          onClick={() => toggleVariety(item.variety)}
                          icon={
                            <Box
                              component="span"
                              sx={{ width: 9, height: 9, borderRadius: "50%", background: color, ml: "8px !important" }}
                            />
                          }
                          label={item.variety}
                          variant={isSelected ? "filled" : "outlined"}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            borderRadius: TOKENS.radius.sm,
                            background: isSelected ? `${color}18` : "#fff",
                            color: isSelected ? TOKENS.colors.textPrimary : TOKENS.colors.textSecondary,
                            border: `1px solid ${isSelected ? `${color}55` : "rgba(15,23,42,0.14)"}`,
                            "& .MuiChip-icon": { order: -1 },
                          }}
                        />
                      </span>
                    </MuiTooltip>
                  );
                })}
              </Stack>
            </Box>
          </>
        )}
      </Box>

      <Divider />

      <Grid container>
        <Grid item xs={12} lg={6} sx={{ borderRight: { lg: `1px solid ${TOKENS.colors.border}` }, borderBottom: { xs: `1px solid ${TOKENS.colors.border}`, lg: "none" } }}>
          <ChartPanel
            icon={TrendingUpRoundedIcon}
            iconColor="#2563EB"
            title="Arrival Trend"
            subtitle={arrivalChartType === "line" ? "Daily arrival, selected varieties" : "Share of total arrival"}
            emptyLabel="No arrival data"
            chartType={arrivalChartType}
            onChartTypeChange={setArrivalChartType}
            lineData={arrivalChartData}
            pieData={arrivalPieData}
            selectedSeries={selectedSeries}
            colorMap={colorMap}
            valueFormatter={formatNumber}
            axisFormatter={formatAxisNumber}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartPanel
            icon={LocalOfferRoundedIcon}
            iconColor="#7C3AED"
            title="Price Trend"
            subtitle={priceChartType === "line" ? "Modal price, selected varieties" : "Modal price by date, selected varieties"}
            emptyLabel="No price data"
            chartType={priceChartType}
            onChartTypeChange={setPriceChartType}
            lineData={priceChartData}
            pieData={pricePieData}
            selectedSeries={selectedSeries}
            colorMap={colorMap}
            valueFormatter={formatCurrency}
            axisFormatter={(v) => `₹${formatAxisNumber(v)}`}
            yAxisStep={50}
            yAxisMin={1500}
            altChartType="bar"
            tooltipNameSuffix=" Price"
            loading={loading}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

export default IVRDashboardCharts;